// ========================================
// COMPOSANT ImageUploader
// Upload d'images vers Cloudinary
// ========================================

import React, { useState, useRef } from 'react';
import { uploadToCloudinary } from '../../../services/cloudinary/config';
import { FiUploadCloud, FiX, FiImage, FiLoader } from 'react-icons/fi';
import './ImageUploader.css';

const ImageUploader = ({ 
  value, 
  onChange, 
  folder = 'portfolio',
  placeholder = 'Cliquez ou glissez une image ici',
  aspectRatio = null,
  maxSize = 5 // MB
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validation du type
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image');
      return;
    }

    // Validation de la taille
    if (file.size > maxSize * 1024 * 1024) {
      setError(`L'image ne doit pas dépasser ${maxSize}MB`);
      return;
    }

    setError(null);
    setIsUploading(true);
    setProgress(0);

    try {
      const result = await uploadToCloudinary(file, folder, (progressValue) => {
        setProgress(progressValue);
      });

      onChange(result.url);
    } catch (err) {
      setError('Erreur lors de l\'upload. Veuillez réessayer.');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleRemove = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="image-uploader">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleInputChange}
        accept="image/*"
        style={{ display: 'none' }}
      />

      {value ? (
        <div className="image-preview">
          <img src={value} alt="Preview" />
          <button 
            type="button"
            className="remove-btn"
            onClick={handleRemove}
            title="Supprimer l'image"
          >
            <FiX />
          </button>
        </div>
      ) : (
        <div 
          className={`upload-zone ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {isUploading ? (
            <div className="upload-progress">
              <FiLoader className="spinner" />
              <p>Upload en cours... {progress}%</p>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="upload-content">
              <FiUploadCloud className="upload-icon" />
              <p className="upload-text">{placeholder}</p>
              <p className="upload-hint">
                <FiImage /> PNG, JPG, GIF jusqu'à {maxSize}MB
              </p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="upload-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;